import {
  Shield,
  Award,
  Users,
  Eye,
  Radio,
  Lock,
  BadgeCheck,
  Clock,
} from "lucide-react";

const pillars = [
  {
    icon: Shield,
    title: "Active & Retired Military",
    desc: "Operators with field-tested judgment and composure under pressure.",
  },
  {
    icon: BadgeCheck,
    title: "Retired Law Enforcement",
    desc: "Sworn professionals trained in de-escalation, observation, and lawful response.",
  },
  {
    icon: Eye,
    title: "Situational Awareness",
    desc: "Pre-emptive threat reading — long before a situation requires action.",
  },
  {
    icon: Clock,
    title: "Rapid, Measured Response",
    desc: "Calm, decisive intervention calibrated to the environment and client.",
  },
  {
    icon: Lock,
    title: "Absolute Discretion",
    desc: "Quiet professionalism. Your operations and people remain confidential.",
  },
  {
    icon: Radio,
    title: "Coordinated Communications",
    desc: "Earpiece, command, and protocol — every detail rehearsed and accountable.",
  },
];

const badges = [
  "Vetted Agents",
  "Background Verified",
  "First-Aid Certified",
  "Executive Etiquette",
  "Defensive Tactics",
  "Threat Assessment",
  "Continuing Education",
  "Confidential Engagements",
];

export default function TrustAuthority() {
  return (
    <section
      id="trust"
      data-testid="trust-section"
      className="relative py-24 sm:py-32 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5 reveal">
            <span className="overline">Authority</span>
            <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-light leading-tight">
              Trained professionals.{" "}
              <span className="text-zinc-500">No exceptions.</span>
            </h2>
            <p className="font-body mt-6 text-zinc-400 leading-relaxed max-w-md">
              The Bridge Collective is built on a single standard: serious
              organizations require serious protection. Every agent is selected
              for skill, demeanor, and the discipline to operate quietly in
              high-stakes environments.
            </p>

            <div className="mt-10 flex items-center gap-4">
              <Users size={18} className="text-[#1A5C40]" strokeWidth={1.4} />
              <span className="text-sm text-zinc-300 tracking-wide">
                Elite professionals. Selectively engaged.
              </span>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-5">
              {pillars.map((p, i) => {
                const Icon = p.icon;
                return (
                  <div
                    key={p.title}
                    data-testid={`pillar-${i}`}
                    className="reveal service-card p-7"
                  >
                    <Icon
                      size={22}
                      className="text-[#1A5C40]"
                      strokeWidth={1.3}
                    />
                    <h3 className="font-display mt-5 text-lg text-white font-medium">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                      {p.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Badges marquee */}
        <div className="mt-20 border-t border-white/5 pt-10 reveal">
          <div className="flex items-center gap-3 mb-6">
            <Award size={14} className="text-[#1A5C40]" strokeWidth={1.4} />
            <span className="overline">Standards Upheld</span>
          </div>
          <div className="overflow-hidden no-scrollbar">
            <div className="marquee-track flex gap-12 whitespace-nowrap">
              {[...badges, ...badges].map((b, i) => (
                <span
                  key={i}
                  className="text-sm tracking-[0.22em] uppercase text-zinc-500 flex items-center gap-3"
                >
                  <span className="h-1 w-1 bg-[#1A5C40] inline-block" />
                  {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
